import { AnyAction, createSlice } from '@reduxjs/toolkit';
import { addToCart, GetAddedAllCarts, removeFromCart } from '../../data-access/apis/common/Carts';
import { Product } from '../../interfaces/common/girls_fashion';
import { ResponseType } from '../../interfaces/response';
type ProductForCart = Omit<Product, 'url'> & {
  _id: string;
  __v: number;
  url: string;  // Add 'url' back here
};

interface InternalState {
  status: ResponseType;
  statusCode: number | null;
  user: any;
  token: string | null;
  loading: boolean;
  errors: string | null;
  url: string | null;
  message: string | null;
  usernameAvailable: boolean | null;
  cartItems: ProductForCart[];
  cartLoaded: boolean;
}

const initialState: InternalState = {
  status: ResponseType.Pending,
  statusCode: null,
  user: null,
  url: null,
  token: null,
  loading: false,
  errors: null,
  message: null,
  usernameAvailable: null,
  cartItems: [],
  cartLoaded: false
};

const internalLifeCycles = createSlice({
  name: 'cartItem',
  initialState,
  reducers: {
    resetContact: (state) => {
      state.status = ResponseType.Pending;
      state.statusCode = null;
      state.user = null;
      state.token = null;
      state.loading = false;
      state.errors = null;
      state.message = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(addToCart.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;

        // 🛒 Push product to cartItems from response data
        if (action.payload && action.payload.data) {
          const productData = action.payload.data;

          state.cartItems.push({
            productId: productData.productId,
            title: productData.title,
            image: productData.image,
            badge: productData.badge,
            url: productData.url,
            _id: productData._id,
            __v: productData.__v,
          });

          state.message = action.payload.message || 'Product added to cart successfully';
        }
      })
      .addCase(addToCart.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;

        const backendMessage = (action.payload?.message || action.payload || '').toString().trim();

        // Normalize both strings before comparing
        const normalizedMessage = backendMessage.normalize("NFKC");

        if (normalizedMessage === 'Invalid authorization token supplied') {
          state.message = 'Login is required to add items to the cart.';
        } else {
          state.message = backendMessage || 'Add to cart failed.';
        }

        state.errors = action.error?.message || null;
      })
      .addCase(GetAddedAllCarts.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetAddedAllCarts.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.message = action.payload.message;
        state.cartItems = action.payload.result || [];
        state.cartLoaded = true;
      })
      .addCase(GetAddedAllCarts.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.cartLoaded = false;

        const backendMessage = (action.payload?.message || action.payload || '').toString().trim();
        const normalizedMessage = backendMessage.normalize('NFKC');

        state.message = normalizedMessage || 'Failed to fetch cart items.';
        state.errors = action.error?.message || null;
      })// 👇 extraReducers me yeh add karo
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeFromCart.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.message = action.payload.message || 'Product removed from cart.';

        const removedId = action.payload.productId;
        state.cartItems = state.cartItems.filter(item => item.productId !== removedId);
      })
      .addCase(removeFromCart.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.message = (action.payload?.message || 'Remove from cart failed.').toString().trim();
        state.errors = action.error?.message || null;
      });
  }
});

export const { resetContact } = internalLifeCycles.actions;
export default internalLifeCycles.reducer;
